import { useEffect, useMemo, useState } from 'react';
import {
  ExternalLink,
  ImagePlus,
  LoaderCircle,
  Plus,
  Pencil,
  QrCode,
  Save,
  Trash2,
} from 'lucide-react';
import { api } from '../lib/api';

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="field">
      <span>{label}</span>
      {children}
    </label>
  );
}

type CommercialPage = {
  id: string;
  type:
    | 'website'
    | 'social'
    | 'next-book'
    | 'newsletter'
    | 'coupon'
    | 'sponsor'
    | 'custom';
  headline: string;
  body: string;
  cta: string;
  destinationUrl: string;
  qrMode: 'generated' | 'uploaded' | 'none';
  qrPath?: string;
  qrUrl?: string | null;
  placement: 'front' | 'back';
  sortOrder: number;
  background: string;
  textColor: string;
};

const templates = [
  {
    id: 'website',
    label: 'Website / shop',
    headline: 'Continue the story online',
    cta: 'Scan to visit',
  },
  {
    id: 'social',
    label: 'Social / community',
    headline: 'Join the community',
    cta: 'Scan to follow',
  },
  {
    id: 'next-book',
    label: 'Next book',
    headline: 'The story continues…',
    cta: 'See the next book',
  },
  {
    id: 'newsletter',
    label: 'Newsletter',
    headline: 'Get new issues first',
    cta: 'Join the list',
  },
  {
    id: 'coupon',
    label: 'Offer / coupon',
    headline: 'A little extra for readers',
    cta: 'Scan to claim',
  },
  {
    id: 'sponsor',
    label: 'Sponsor / partner',
    headline: 'Supported by',
    cta: 'Learn more',
  },
  {
    id: 'custom',
    label: 'Custom CTA',
    headline: 'Stay connected',
    cta: 'Scan here',
  },
] as const;

export default function CommercialPagesBuilder({
  projectId,
}: {
  projectId: string;
}) {
  const [items, setItems] = useState<CommercialPage[]>([]);
  const [editing, setEditing] = useState<CommercialPage | null>(null);
  const [busy, setBusy] = useState('');
  const [message, setMessage] = useState('');

  const load = async () => {
    const { data } = await api.get<{ items: CommercialPage[] }>(
      `/api/projects/${projectId}/commercial-pages`
    );
    setItems(data.items);
  };

  useEffect(() => {
    load().catch(() => setMessage('Commercial pages could not be loaded.'));
  }, [projectId]);

  const createFromTemplate = async (templateId: string) => {
    const template = templates.find(item => item.id === templateId)!;
    setBusy('create');
    try {
      const { data } = await api.post<CommercialPage>(
        `/api/projects/${projectId}/commercial-pages`,
        {
          type: template.id,
          headline: template.headline,
          body: '',
          cta: template.cta,
          destinationUrl: '',
          qrMode: 'none',
          placement: 'back',
          background: '#17191d',
          textColor: '#ffffff',
        }
      );
      await load();
      setEditing(data);
    } finally {
      setBusy('');
    }
  };

  return (
    <section className="card p-6 sm:p-8">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <span className="eyebrow text-[#ef5b3e]">COMMERCIAL PAGES</span>
          <h2 className="mt-2 text-3xl font-black">
            Add useful pages after the story.
          </h2>
          <p className="mt-2 max-w-2xl text-sm leading-relaxed text-black/50">
            Create website, shop, social, newsletter, next-book, sponsor or
            custom CTA pages. Generate a QR from a URL or upload your own QR.
          </p>
        </div>
      </div>

      <div className="mt-6 flex flex-wrap gap-2">
        {templates.map(template => (
          <button
            type="button"
            key={template.id}
            className="secondary"
            onClick={() => createFromTemplate(template.id)}
            disabled={busy === 'create'}
          >
            <Plus size={15} /> {template.label}
          </button>
        ))}
      </div>

      <div className="mt-7 grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {items.map(item => (
          <article
            key={item.id}
            className="commercialCard"
          >
            <div
              className="aspect-[7/10] p-5"
              style={{
                background: item.background,
                color: item.textColor,
              }}
            >
              <div className="flex h-full flex-col">
                <div className="text-[10px] font-black uppercase tracking-widest opacity-60">
                  {item.type.replace('-', ' ')}
                </div>
                <div className="mt-auto">
                  <div className="text-2xl font-black leading-tight">
                    {item.headline}
                  </div>
                  <p className="mt-3 text-sm opacity-75">
                    {item.body || 'Commercial message…'}
                  </p>
                  {item.qrUrl && (
                    <img
                      src={item.qrUrl}
                      alt="QR"
                      className="mt-5 size-28 rounded bg-white p-2"
                    />
                  )}
                  <div className="mt-3 text-xs font-black uppercase tracking-wider">
                    {item.cta}
                  </div>
                </div>
              </div>
            </div>
            <div className="p-4">
              <div className="row items-start"><div><b>{item.headline}</b><div className="mt-1 text-xs text-black/40">{item.placement} matter · QR {item.qrMode}</div></div><div className="flex gap-1"><button className="iconButton" title="Edit page" onClick={()=>setEditing(item)}><Pencil size={14}/></button><button className="iconButton dangerIcon" title="Delete page" onClick={async()=>{if(!window.confirm(`Delete commercial page “${item.headline}”?`))return;await api.delete(`/api/projects/${projectId}/commercial-pages/${item.id}`);await load()}}><Trash2 size={14}/></button></div></div>
            </div>
          </article>
        ))}
      </div>

      {editing && (
        <CommercialPageModal
          projectId={projectId}
          page={editing}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await load();
          }}
        />
      )}

      {message && (
        <div className="mt-5 rounded-2xl bg-[#f1eee6] p-4 text-sm text-black/60">
          {message}
        </div>
      )}
    </section>
  );
}

function CommercialPageModal({
  projectId,
  page,
  onClose,
  onSaved,
}: {
  projectId: string;
  page: CommercialPage;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const [form, setForm] = useState(page);
  const [busy, setBusy] = useState('');
  const [errorText, setErrorText] = useState('');

  const generateQr = async () => {
    if (!form.destinationUrl.trim()) {
      setErrorText('Add a URL before generating the QR.');
      return;
    }
    setBusy('qr');
    setErrorText('');
    try {
      const { data } = await api.post<{ path: string; url: string }>(
        `/api/projects/${projectId}/assets/qr`,
        {
          url: form.destinationUrl,
          commercialPageId: page.id,
        }
      );
      setForm({
        ...form,
        qrMode: 'generated',
        qrPath: data.path,
        qrUrl: data.url,
      });
    } catch {
      setErrorText('The QR code could not be generated.');
    } finally {
      setBusy('');
    }
  };

  const uploadQr = async (file: File | null) => {
    if (!file) return;
    setBusy('upload');
    setErrorText('');
    try {
      const upload = new FormData();
      upload.append('commercialPageId', page.id);
      upload.append('file', file);
      const { data } = await api.postForm<{ path: string; url: string }>(
        `/api/projects/${projectId}/assets/qr-upload`,
        upload
      );
      setForm({
        ...form,
        qrMode: 'uploaded',
        qrPath: data.path,
        qrUrl: data.url,
      });
    } catch {
      setErrorText('The QR image could not be uploaded.');
    } finally {
      setBusy('');
    }
  };

  const save = async () => {
    setBusy('save');
    setErrorText('');
    try {
      await api.put(
        `/api/projects/${projectId}/commercial-pages/${page.id}`,
        form
      );
      await onSaved();
    } catch {
      setErrorText('Commercial page changes could not be saved.');
    } finally {
      setBusy('');
    }
  };

  const remove = async () => {
    setBusy('delete');
    try {
      await api.delete(
        `/api/projects/${projectId}/commercial-pages/${page.id}`
      );
      await onSaved();
    } finally {
      setBusy('');
    }
  };

  return (
    <div className="fixed inset-0 z-[60] grid place-items-center bg-black/50 p-4 backdrop-blur-sm">
      <div className="max-h-[94vh] w-full max-w-5xl overflow-y-auto rounded-[28px] bg-[#fbfaf6] p-6 shadow-2xl sm:p-8">
        <div className="flex justify-between gap-4">
          <div>
            <span className="eyebrow text-[#ef5b3e]">COMMERCIAL PAGE</span>
            <h3 className="mt-2 text-3xl font-black">Build the reader CTA.</h3>
          </div>
          <button onClick={onClose} className="iconButton">
            ×
          </button>
        </div>

        <div className="mt-7 grid gap-6 lg:grid-cols-[1fr_360px]">
          <section
            className="mx-auto aspect-[7/10] w-full max-w-[430px] rounded-xl p-8 shadow-xl"
            style={{ background: form.background, color: form.textColor }}
          >
            <div className="flex h-full flex-col">
              <div className="text-xs font-black uppercase tracking-[.16em] opacity-55">
                {form.type.replace('-', ' ')}
              </div>
              <div className="mt-auto">
                <div className="text-4xl font-black leading-[.95]">
                  {form.headline}
                </div>
                <p className="mt-5 leading-relaxed opacity-75">
                  {form.body || 'Your reader message appears here.'}
                </p>
                {form.qrUrl && (
                  <img
                    src={form.qrUrl}
                    alt="QR code"
                    className="mt-7 size-36 bg-white p-2"
                  />
                )}
                <div className="mt-4 font-black uppercase tracking-widest">
                  {form.cta}
                </div>
              </div>
            </div>
          </section>

          <aside className="space-y-4">
            <Field label="Headline">
              <input
                value={form.headline}
                onChange={event =>
                  setForm({ ...form, headline: event.target.value })
                }
              />
            </Field>
            <Field label="Body">
              <textarea
                rows={5}
                value={form.body}
                onChange={event =>
                  setForm({ ...form, body: event.target.value })
                }
              />
            </Field>
            <Field label="CTA">
              <input
                value={form.cta}
                onChange={event =>
                  setForm({ ...form, cta: event.target.value })
                }
              />
            </Field>
            <Field label="URL">
              <input
                type="url"
                value={form.destinationUrl}
                onChange={event =>
                  setForm({ ...form, destinationUrl: event.target.value })
                }
                placeholder="https://example.com"
              />
            </Field>

            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={generateQr}
                disabled={busy === 'qr'}
                className="secondary"
              >
                {busy === 'qr' ? (
                  <LoaderCircle size={15} className="animate-spin" />
                ) : (
                  <QrCode size={15} />
                )}
                Generate QR
              </button>

              <label className="secondary cursor-pointer">
                <ImagePlus size={15} /> Upload QR
                <input
                  className="hidden"
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  onChange={event =>
                    uploadQr(event.target.files?.[0] || null)
                  }
                />
              </label>
            </div>

            {form.destinationUrl && (
              <a
                href={form.destinationUrl}
                target="_blank"
                rel="noreferrer"
                className="secondary w-full"
              >
                <ExternalLink size={15} /> Test destination
              </a>
            )}

            <div className="grid grid-cols-2 gap-3">
              <Field label="Background">
                <input
                  type="color"
                  value={form.background}
                  onChange={event =>
                    setForm({ ...form, background: event.target.value })
                  }
                />
              </Field>
              <Field label="Text">
                <input
                  type="color"
                  value={form.textColor}
                  onChange={event =>
                    setForm({ ...form, textColor: event.target.value })
                  }
                />
              </Field>
            </div>

            <Field label="Placement">
              <select
                value={form.placement}
                onChange={event =>
                  setForm({
                    ...form,
                    placement: event.target.value as 'front' | 'back',
                  })
                }
              >
                <option value="back">Back matter</option>
                <option value="front">Front matter</option>
              </select>
            </Field>

            <div className="grid grid-cols-2 gap-2 pt-2">
              <button
                onClick={remove}
                disabled={busy === 'delete'}
                className="secondary"
              >
                <Trash2 size={15} /> Delete
              </button>
              <button
                onClick={save}
                disabled={busy === 'save'}
                className="primary"
              >
                <Save size={15} /> Save
              </button>
            </div>
          </aside>
        </div>

        {errorText && (
          <div className="mt-5 rounded-2xl border border-[#ef5b3e]/20 bg-[#fff1ec] p-4 text-sm text-[#8d3526]">
            {errorText}
          </div>
        )}
      </div>
    </div>
  );
}

